import RenderObject from "../render/renderobject";
import ShaderService from "../shader/shader_service";
import ResourceService from "../resource/resourceservice";
import Camera from "./camera";
import { CollisionVolume } from "../collision/collision_volume";
import CollisionService from "../collision/collision_service";
import Placement from "./placement";
import VolumePlacement from "../collision/volume_placement";
import Attachment from "../animation/attachment";

export default class SceneObject extends RenderObject
{
     children: SceneObject[] = [];
     collisionVolumes: CollisionVolume[] = [];
     placement:Placement;
     volume_placement: VolumePlacement;
     attachments: Attachment[] = [];
     attached_children: { child: SceneObject; attachment_name: string }[] = [];

    constructor(key:string)
    {
        super(key);
        this.placement = new Placement(this);
        this.volume_placement = new VolumePlacement(this);
    }

     add_child(child: SceneObject): void 
     {
        this.children.push(child);
     }

     remove_child(child: SceneObject): void 
     {
      const idx = this.children.findIndex((so) => so === child);
      if (idx >= 0) 
      {
            this.children.splice(idx, 1);
      }
  }
      

      add_collision_volume(volume: CollisionVolume): void {
    volume.owner = this;
    this.collisionVolumes.push(volume);
  }

  add_attachment(attachment: Attachment): void 
  {
     this.attachments.push(attachment);
  }

get_attachment(name: string): Attachment | null {
  for (const a of this.attachments) {
    if (a.name === name) return a;
  }
  return null;
}

attach_child(child: SceneObject, attachment_name: string): void {
  this.attached_children.push({ child, attachment_name });
}

detach_child(child: SceneObject): void {
  const idx = this.attached_children.findIndex((ac) => ac.child === child);
  if (idx >= 0) {
    this.attached_children.splice(idx, 1);
  }
}

update_attachments(): void {
  for (const attachment of this.attachments) {
    attachment.update(this);
  }

  for (const ac of this.attached_children) {
    const attachment = this.get_attachment(ac.attachment_name);
    if (!attachment) continue;

    // Preserve the complete joint orientation, but strip inherited scale so
    // the attached object retains its own authored scale.
    const child = ac.child;
    const world = new Float32Array(attachment.world_matrix);
    const child_scale = child.transform.scale;

    for (let column = 0; column < 3; column++) {
      const base = column * 4;
      const length = Math.hypot(world[base], world[base + 1], world[base + 2]);
      const scale = child_scale[column];
      if (length > 0.000001) {
        world[base] = (world[base] / length) * scale;
        world[base + 1] = (world[base + 1] / length) * scale;
        world[base + 2] = (world[base + 2] / length) * scale;
      }
    }

    child.transform.set_from_matrix(world);

  }
}

  register_collisions(collisionService: CollisionService): void {
    for (const volume of this.collisionVolumes) {
      collisionService.register(volume);
    }

    for (const child of this.children) {
      child.register_collisions(collisionService);
    }
  }

  unregister_collisions(collisionService: CollisionService): void {
    for (const volume of this.collisionVolumes) {
      collisionService.unregister(volume);
    }

    for (const child of this.children) {
      child.unregister_collisions(collisionService);
    }
  }



    async build(device: GPUDevice,format: GPUTextureFormat,shader_service: ShaderService, resource_service: ResourceService): Promise<void> 
    {
        for (const child of this.children) {
        await child.build(device, format, shader_service, resource_service);
        }
    }

    tick(delta_time: number): void 
    {
        for (const child of this.children) 
        {
            child.tick(delta_time);
        }
  }

  compute(
    device: GPUDevice,
    pass: GPUComputePassEncoder,
    _encoder: GPUCommandEncoder
  ): void 
  {
    for (const child of this.children) {
      child.compute(device,pass,_encoder);
    }
  }

  prepare(device: GPUDevice, camera: Camera): void
  {
    for (const child of this.children) {
      child.prepare(device, camera);
    }
  }

  draw(pass: GPURenderPassEncoder): void 
  {
    for (const child of this.children) {
      child.draw(pass);
    }
  }

  finish_draw(): void 
  {
      for (const child of this.children) {
      child.finish_draw();
    }
  }

  ui_message(cmd: string, msg: string): void 
  {
    for (const child of this.children)
    {
      child.ui_message(cmd, msg);
    }
  }
  
}